// Admin API client — /admin/affiliates/* (mirrors the API's affiliates aggregate).

import type {
  AffiliateAdminLink,
  AffiliateCampaign,
  AffiliateCouponCode,
  AffiliateDetailResponse,
  AffiliateListResponse,
  ApprovePayoutResult,
  AuditLogResponse,
  CommissionListResponse,
  ExecutePayoutResult,
  PayoutDetailResponse,
  PayoutListResponse,
  ProgramOverviewEnvelope,
  ProgramOverviewResponse,
  ReconciliationReport,
  ReconciliationResponse,
  SuspiciousQueueResponse,
  SuspiciousVerdict,
} from '@/pages/affiliates/types';
import { client, rawAdminFetch, rawJson } from './transport';

export const affiliatesApi = {
  // ------------------------------------------------------------------------------------
  // Affiliate program (A7Y-1898). All paths are relative to the console's
  // `/admin` baseUrl and nested under `/affiliates` (the admin_affiliates_route
  // sub-router). Centralised here so a BE routing change touches one file.
  affiliates: {
    // --- Affiliates ---
    list: (params?: { status?: string; search?: string }) => {
      const sp = new URLSearchParams();
      if (params?.status && params.status !== 'all') sp.set('status', params.status);
      if (params?.search) sp.set('search', params.search);
      const qs = sp.toString();
      return client.get<AffiliateListResponse>(`/affiliates${qs ? `?${qs}` : ''}`);
    },

    get: (id: string) => client.get<AffiliateDetailResponse>(`/affiliates/${id}`),

    approve: (id: string) => client.post<{ ok: boolean }>(`/affiliates/${id}/approve`),

    disable: (id: string) => client.post<{ ok: boolean }>(`/affiliates/${id}/disable`),

    reenable: (id: string) => client.post<{ ok: boolean }>(`/affiliates/${id}/reenable`),

    gdprAnonymize: (id: string) =>
      client.post<{ ok: boolean; message?: string }>(`/affiliates/${id}/gdpr-anonymize`),

    getAuditLog: (id: string) => client.get<AuditLogResponse>(`/affiliates/${id}/audit-log`),

    // --- Links ---
    createLink: async (
      id: string
    ): Promise<
      { ok: true; link: AffiliateAdminLink } | { ok: false; duplicate: true; message: string }
    > => {
      const res = await rawAdminFetch(`/affiliates/${id}/links`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        return { ok: false, duplicate: true, message: body.message ?? 'Duplicate tracking token' };
      }
      if (!res.ok) {
        throw new Error(`Create link failed: ${res.status} ${res.statusText}`);
      }
      // CONTRACT: success body is `{ ok: true, link }` — read `.link`.
      const body = (await res.json()) as { ok: boolean; link: AffiliateAdminLink };
      return { ok: true, link: body.link };
    },

    archiveLink: (id: string, linkId: string) =>
      client.delete<{ ok: boolean }>(`/affiliates/${id}/links/${linkId}`),

    // --- Coupon + customer discount ---
    setDiscount: (id: string, customerDiscountPercent: number | null) =>
      rawJson<{ ok: boolean; coupon: AffiliateCouponCode | null }>(
        `/affiliates/${id}/discount`,
        'PUT',
        { customerDiscountPercent }
      ),

    // CONTRACT: coupon get/create/update → `{ coupon }`; unwrap to the bare doc.
    getCoupon: async (id: string): Promise<AffiliateCouponCode | null> => {
      const res = await client.get<{ coupon: AffiliateCouponCode | null }>(
        `/affiliates/${id}/coupon`
      );
      return res.coupon;
    },

    createCoupon: async (id: string, code: string): Promise<AffiliateCouponCode> => {
      const res = await client.post<{ coupon: AffiliateCouponCode }>(`/affiliates/${id}/coupon`, {
        code,
      });
      return res.coupon;
    },

    updateCoupon: async (id: string, code: string): Promise<AffiliateCouponCode> => {
      const res = await rawJson<{ coupon: AffiliateCouponCode }>(
        `/affiliates/${id}/coupon`,
        'PUT',
        { code }
      );
      return res.coupon;
    },

    // --- Suspicious / fraud queue ---
    listSuspiciousQueue: () => client.get<SuspiciousQueueResponse>(`/affiliates/queues/suspicious`),

    resolveSuspicious: (
      affiliateId: string,
      body: { referralId: string; verdict: SuspiciousVerdict }
    ) => client.post<{ ok: boolean }>(`/affiliates/${affiliateId}/suspicious/resolve`, body),

    // --- First-commission hold queue ---
    listFirstHoldQueue: () =>
      client.get<CommissionListResponse>(`/affiliates/commissions/queues/first-hold`),

    releaseFirstHold: (commissionId: string) =>
      client.post<{ ok: boolean }>(`/affiliates/commissions/${commissionId}/release-hold`),

    // --- Commissions ---
    listCommissions: (params?: { state?: string; affiliateId?: string }) => {
      const sp = new URLSearchParams();
      if (params?.state && params.state !== 'all') sp.set('state', params.state);
      if (params?.affiliateId) sp.set('affiliateId', params.affiliateId);
      const qs = sp.toString();
      return client.get<CommissionListResponse>(`/affiliates/commissions${qs ? `?${qs}` : ''}`);
    },

    // CONTRACT: void → `{ ok }`.
    voidCommission: (id: string, reason: string) =>
      client.post<{ ok: boolean }>(`/affiliates/commissions/${id}/void`, { reason }),

    // CONTRACT: adjust → `{ ok, amountCents }`.
    adjustCommission: (id: string, body: { amountCents: number; reason: string }) =>
      client.post<{ ok: boolean; amountCents: number }>(
        `/affiliates/commissions/${id}/adjust`,
        body
      ),

    listClawbacks: () =>
      client.get<CommissionListResponse>(`/affiliates/commissions/queues/clawback`),

    // --- Campaigns ---
    // CONTRACT: list → `{ campaigns }`; get/create/update → `{ campaign }` (unwrapped).
    listCampaigns: () => client.get<{ campaigns: AffiliateCampaign[] }>(`/affiliates/campaigns`),

    getCampaign: async (id: string): Promise<AffiliateCampaign> => {
      const res = await client.get<{ campaign: AffiliateCampaign }>(`/affiliates/campaigns/${id}`);
      return res.campaign;
    },

    createCampaign: async (body: Partial<AffiliateCampaign>): Promise<AffiliateCampaign> => {
      const res = await client.post<{ campaign: AffiliateCampaign }>(`/affiliates/campaigns`, body);
      return res.campaign;
    },

    updateCampaign: async (
      id: string,
      body: Partial<AffiliateCampaign>
    ): Promise<AffiliateCampaign> => {
      const res = await rawJson<{ campaign: AffiliateCampaign }>(
        `/affiliates/campaigns/${id}`,
        'PUT',
        body
      );
      return res.campaign;
    },

    setDefaultCampaign: (id: string) =>
      client.post<{ ok: boolean }>(`/affiliates/campaigns/${id}/set-default`),

    // --- Payouts (approval + state only; execution is A7Y-1899) ---
    listPayouts: (params?: { state?: string }) => {
      const sp = new URLSearchParams();
      if (params?.state && params.state !== 'all') sp.set('state', params.state);
      const qs = sp.toString();
      return client.get<PayoutListResponse>(`/affiliates/payouts${qs ? `?${qs}` : ''}`);
    },

    getPayout: (id: string) => client.get<PayoutDetailResponse>(`/affiliates/payouts/${id}`),

    /**
     * Approve a payout run. Over the run hard-cap the BE replies 409 with
     * `{ requiresConfirmation, cap, amount }`; the caller then re-invokes with
     * `confirm: true` after a second confirmation. Returns a discriminated
     * result rather than throwing so the UI can branch cleanly.
     */
    approvePayout: async (id: string, confirm?: boolean): Promise<ApprovePayoutResult> => {
      const res = await rawAdminFetch(`/affiliates/payouts/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(confirm ? { confirm: true } : {}),
      });
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          requiresConfirmation?: boolean;
          cap?: number;
          amount?: number;
          currency?: string;
        };
        return {
          ok: false,
          requiresConfirmation: true,
          cap: body.cap ?? 0,
          amount: body.amount ?? 0,
          currency: body.currency,
        };
      }
      if (!res.ok) {
        throw new Error(`Approve payout failed: ${res.status} ${res.statusText}`);
      }
      // CONTRACT: success body is `{ ok: true, payout }` — read `.payout`.
      const body = (await res.json()) as { ok: boolean; payout: PayoutDetailResponse['payout'] };
      return { ok: true, payout: body.payout };
    },

    /**
     * Execute an approved payout (A7Y-1899) — IRREVERSIBLE money movement via
     * Stripe Global Payouts. Distinct from approve: only an approved, still
     * `scheduled` run is eligible. Returns a discriminated result rather than
     * throwing so the UI can surface the structured error codes
     * (`insufficient_balance` / `recipient_restricted` / `already_executed`).
     */
    executePayout: async (id: string): Promise<ExecutePayoutResult> => {
      const res = await rawAdminFetch(`/affiliates/payouts/${id}/execute`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const body = (await res.json().catch(() => null)) as
        { ok: true; payout: PayoutDetailResponse['payout'] } | { ok: false; error: string } | null;
      if (body && body.ok === true) {
        return { ok: true, payout: body.payout };
      }
      if (body && body.ok === false && typeof body.error === 'string') {
        return { ok: false, error: body.error };
      }
      throw new Error(`Execute payout failed: ${res.status} ${res.statusText}`);
    },

    markPayoutExternal: (id: string, body: { method: string; reference: string }) =>
      client.post<{ ok: boolean }>(`/affiliates/payouts/${id}/mark-paid-external`, body),

    /** Download the payouts CSV (month-end liability snapshot); returns a Blob. */
    exportPayoutsCsv: async (params?: { state?: string }): Promise<Blob> => {
      const sp = new URLSearchParams();
      if (params?.state && params.state !== 'all') sp.set('state', params.state);
      const qs = sp.toString();
      const res = await rawAdminFetch(`/affiliates/payouts/export.csv${qs ? `?${qs}` : ''}`, {
        method: 'GET',
      });
      if (!res.ok) throw new Error(`CSV export failed: ${res.status} ${res.statusText}`);
      return res.blob();
    },

    // --- Reconciliation + overview ---
    // CONTRACT: reconciliation → `{ report }`; overview → `{ overview }` (unwrapped).
    getReconciliation: async (): Promise<ReconciliationReport> => {
      const res = await client.get<ReconciliationResponse>(`/affiliates/reconciliation`);
      return res.report;
    },

    getOverview: async (): Promise<ProgramOverviewResponse> => {
      const res = await client.get<ProgramOverviewEnvelope>(`/affiliates/overview`);
      return res.overview;
    },
  },
};
